import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Link2, Search, CalendarDays, MapPin } from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return d; }
}

/**
 * Modal that lists every entry from the global Events Database and lets
 * an admin link one or more of them to a cohort. Purely additive — the
 * source events are untouched; only a `cohort_event_links` row is created.
 */
export default function LinkGlobalEventModal({ open, onOpenChange, cohortId, existingLinkedIds = [], onLinked }) {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQuery("");
    setLoading(true);
    axios.get(`${API}/events`, { withCredentials: true })
      .then(({ data }) => setEvents((data && data.events) || []))
      .catch(() => toast.error("Failed to load Events Database"))
      .finally(() => setLoading(false));
  }, [open]);

  const alreadyLinked = useMemo(() => new Set(existingLinkedIds || []), [existingLinkedIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) => (
      (e.name || "").toLowerCase().includes(q)
      || (e.location || "").toLowerCase().includes(q)
      || (e.date || "").toLowerCase().includes(q)
    ));
  }, [events, query]);

  const toggle = (id) => {
    if (alreadyLinked.has(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const linkSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setSaving(true);
    let ok = 0;
    let fail = 0;
    let attendeesAdded = 0;
    for (const id of ids) {
      try {
        const { data } = await axios.post(
          `${API}/cohorts/${cohortId}/events/link`,
          { event_id: id },
          { withCredentials: true },
        );
        ok += 1;
        attendeesAdded += data?.attendees_added || 0;
      } catch {
        fail += 1;
      }
    }
    setSaving(false);
    if (ok) {
      const suffix = attendeesAdded
        ? ` · ${attendeesAdded} attendee${attendeesAdded === 1 ? "" : "s"} added from cohort roster`
        : "";
      toast.success(`Linked ${ok} event${ok === 1 ? "" : "s"} to this cohort${suffix}`);
    }
    if (fail) toast.error(`${fail} link${fail === 1 ? "" : "s"} failed`);
    onOpenChange(false);
    if (onLinked) onLinked();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-2xl max-h-[85vh] flex flex-col" data-testid="link-global-event-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-white flex items-center gap-2">
            <Link2 className="h-4 w-4" /> Link Existing Event
          </DialogTitle>
          <p className="text-xs font-body text-[#6E7787]">
            Attach an event from your Events Database (/admin/events) to this cohort. The source event is unchanged.
          </p>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, location, or date"
            data-testid="link-event-search"
            className="pl-9 bg-[#0A0C10] border-white/10 text-white"
          />
        </div>

        <div className="flex-1 overflow-y-auto -mx-2 px-2" data-testid="link-event-list">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-[#6E7787]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-sm font-body text-[#6E7787]" data-testid="link-event-empty">
              {events.length === 0
                ? "No events in the Events Database yet. Create one at /admin/events."
                : "No events match your search."}
            </div>
          ) : (
            <ul className="space-y-2">
              {filtered.map((e) => {
                const isLinked = alreadyLinked.has(e.id);
                const isSelected = selected.has(e.id);
                return (
                  <li
                    key={e.id}
                    onClick={() => toggle(e.id)}
                    data-testid={`link-event-row-${e.id}`}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-3 transition-all ${
                      isLinked
                        ? "border-white/5 opacity-50 cursor-not-allowed"
                        : isSelected
                          ? "border-[var(--primary-color,#4A90D9)]/60 ring-1 ring-[var(--primary-color,#4A90D9)]/30 bg-[var(--primary-color,#4A90D9)]/5 cursor-pointer"
                          : "border-white/10 hover:border-white/20 cursor-pointer bg-[#0A0C10]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isLinked}
                      onChange={() => toggle(e.id)}
                      onClick={(evt) => evt.stopPropagation()}
                      data-testid={`link-event-checkbox-${e.id}`}
                      className="h-4 w-4 rounded border-white/20 bg-transparent accent-[var(--primary-color,#4A90D9)] cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-body font-medium text-white truncate">
                        {e.name}
                        {isLinked && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-400/80">
                            Already linked
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-1 text-xs font-body text-[#A1A9B8]">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="h-3 w-3" /> {formatDate(e.date)}{e.start_time ? ` · ${e.start_time}` : ""}
                        </span>
                        {e.location && (
                          <span className="inline-flex items-center gap-1 truncate max-w-[240px]">
                            <MapPin className="h-3 w-3" /> {e.location}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="border-t border-white/5 pt-3">
          <div className="flex-1 text-xs font-body text-[#6E7787]" data-testid="link-event-selected-count">
            {selected.size} selected
          </div>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="link-event-cancel"
            className="text-[#A1A9B8] hover:text-white hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            onClick={linkSelected}
            disabled={saving || selected.size === 0}
            data-testid="link-event-confirm"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          >
            {saving
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : `Link ${selected.size || ""} event${selected.size === 1 ? "" : "s"}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
